import React, { useState, useEffect } from 'react';
import { createUser, updateUser, getCenters, getAdminCenterAllocations } from '../../../services/supabaseService';
import { useAuth } from '../../../components/hooks/useAuth';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Badge from '../../../components/ui/Badge';

const UserFormModal = ({ user, onClose, onSuccess }) => {
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    role: 'sales',
    centerId: '',
    phone: '',
    isActive: true,
  });
  const [centers, setCenters] = useState([]);
  const [allocatedCenters, setAllocatedCenters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCenters();
    if (user) {
      setFormData({
        fullName: user?.fullName || '',
        email: user?.email || '',
        password: '',
        role: user?.role || 'sales',
        centerId: user?.centerId || '',
        phone: user?.phone || '',
        isActive: user?.isActive !== undefined ? user?.isActive : true,
      });
      
      // Fetch allocated centers if user is admin
      if (user?.role === 'admin' && user?.id) {
        fetchAllocatedCenters(user?.id);
      }
    }
  }, [user]);

  const fetchCenters = async () => {
    try {
      const data = await getCenters();
      setCenters(data || []);
    } catch (err) {
      console.error('Error fetching centers:', err);
    }
  };

  const fetchAllocatedCenters = async (userId) => {
    try {
      const data = await getAdminCenterAllocations(userId);
      setAllocatedCenters(data || []);
    } catch (err) {
      console.error('Error fetching allocated centers:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Update existing user
        await updateUser(user?.id, {
          fullName: formData?.fullName,
          role: formData?.role,
          centerId: formData?.centerId || null,
          phone: formData?.phone || null,
          isActive: formData?.isActive,
        });
      } else {
        // Create new user
        if (!formData?.password) {
          setError('Password is required for new users');
          setLoading(false);
          return;
        }
        await createUser(formData);
      }
      onSuccess?.();
    } catch (err) {
      console.error('Error saving user:', err);
      setError(err?.message || 'Failed to save user');
    } finally {
      setLoading(false);
    }
  };

  const getRoleOptions = () => {
    if (userProfile?.role === 'super_admin') {
      return [
        { value: 'super_admin', label: 'Super Admin' },
        { value: 'admin', label: 'Admin/Owner' },
        { value: 'center_manager', label: 'Center Manager Sales' },
        { value: 'center_manager_ops', label: 'Center Manager Ops' },
        { value: 'sales', label: 'Sales' },
      ];
    } else if (userProfile?.role === 'admin') {
      return [
        { value: 'center_manager', label: 'Center Manager Sales' },
        { value: 'center_manager_ops', label: 'Center Manager Ops' },
        { value: 'sales', label: 'Sales' },
      ];
    }
    return [{ value: 'sales', label: 'Sales' }];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">{user ? 'Edit User' : 'Add New User'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
              <Icon name="AlertCircle" size={20} className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Show allocated centers for admin users */}
          {user?.role === 'admin' && allocatedCenters?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Building" size={16} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-900">Allocated Centers:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {allocatedCenters?.map((alloc) => (
                  <Badge key={alloc?.id} variant="blue">
                    {alloc?.centers?.name || alloc?.centerId}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {/* Show parent admin info */}
          {user?.parentAdmin && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="Users" size={16} className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Reports to:</span>
                <span className="text-sm text-gray-900">{user?.parentAdmin?.fullName}</span>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
              <Input
                type="text"
                name="fullName"
                value={formData?.fullName}
                onChange={handleChange}
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <Input
                type="email"
                name="email"
                value={formData?.email}
                onChange={handleChange}
                placeholder="john@example.com"
                required
                disabled={!!user}
              />
            </div>

            {!user && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password *</label>
                <Input
                  type="password"
                  name="password"
                  value={formData?.password}
                  onChange={handleChange}
                  placeholder="Minimum 8 characters"
                  required={!user}
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role *</label>
              <Select name="role" value={formData?.role} onChange={handleChange} required>
                {getRoleOptions()?.map(option => (
                  <option key={option?.value} value={option?.value}>{option?.label}</option>
                ))}
              </Select>
            </div>

            {/* Show center selection for non-admin roles */}
            {!['super_admin', 'admin']?.includes(formData?.role) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Center</label>
                <Select name="centerId" value={formData?.centerId} onChange={handleChange}>
                  <option value="">No Center Assigned</option>
                  {centers?.map((center) => (
                    <option key={center?.id} value={center?.id}>
                      {center?.name} - {center?.location}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <Input
                type="tel"
                name="phone"
                value={formData?.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
              />
            </div>

            {user && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData?.isActive}
                  onChange={handleChange}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                />
                <label className="text-sm font-medium text-gray-700">Active User</label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Saving...' : user ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserFormModal;